import { Base64 } from "js-base64";
import {
  capitalize,
  compact,
  endsWith,
  find,
  flatten,
  head,
  isEmpty,
  map,
  orderBy,
  pick,
  uniqBy,
  values
} from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import ExternalLink from "../../../components/ExternalLink";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import { useCommissionReports } from "../../../context/commissionReports";
import { getCommissionReportTitle } from "../../../context/commissionReports/CommissionReportsProvider";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { usePaymentTransactions } from "../../../context/paymentTransactions";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import { PaymentTransaction } from "../../../utils/types";
import { useHntMarket } from "../../../utils/useHntMarket";
import { renderDate } from "../../Map/HotspotDetails/HotspotInfoTab";
import EntityDataTable from "./EntityDataTable";
import PaymentTransactionSidebar, {
  DEFAULT_PAYMENT_MEMO
} from "./PaymentTransactionSidebar";
import TablePropertiesSidebar from "./TablePropertiesSidebar";
import useDataTable, { Property } from "./useDataTable";
import { PaginationKey } from "./usePagination";

interface Props {
  className?: string;
  paginationKey: PaginationKey;
  title: string;
  walletAddresses: string[];
  loading?: boolean;
}

const PaymentTransactionsDataTable: React.FC<Props> = ({
  className,
  paginationKey,
  title,
  walletAddresses,
  loading = false
}) => {
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable(
    (state) => state.sortOrder[Entity.transaction]
  );
  const { displayInCurrency } = useHntMarket();
  const { getIsReady } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] =
    useState<PaymentTransaction | null>(null);
  const { commissionReports } = useCommissionReports();
  const { paymentTransactionsByWallet, fetchPaymentTransactions } =
    usePaymentTransactions();
  const [isFetched, setIsFetched] = useState(false);

  const walletAddressesToFetch = useMemo(
    () => walletAddresses.slice(0, PAYMENT_TRANSACTIONS_MAX_WALLETS_TO_FETCH),
    [walletAddresses]
  );

  const isLoading = useMemo(
    () =>
      (isEmpty(walletAddressesToFetch) && loading) ||
      !getIsReady([LoadingKey.paymentTransactions]),
    [getIsReady, loading, walletAddressesToFetch]
  );

  useEffect(() => {
    if (!isLoading && !isFetched) {
      fetchPaymentTransactions(walletAddressesToFetch);
      setIsFetched(true);
    }
  }, [fetchPaymentTransactions, isFetched, isLoading, walletAddressesToFetch]);

  const properties: { [key: string]: Property<PaymentTransaction> } = useMemo(
    () => ({
      date: {
        key: "date",
        title: "Date",
        sortIteratee: "time",
        style: { minWidth: "16rem" },
        renderContent: (transaction) => renderDate(transaction.time)
      },
      amount: {
        key: "amount",
        title: "Tx amount",
        style: { minWidth: "10rem" },
        sortIteratee: "total_amount",
        renderContent: (transaction) => {
          if (!transaction.total_amount) return null;

          return displayInCurrency(transaction.total_amount, {
            needsDivision: true,
            withHntSuffix: true
          });
        }
      },
      sender: {
        key: "sender",
        title: "Sender",
        style: { minWidth: "12rem" },
        renderContent: (transaction) => (
          <DisplayLabels contextWalletAddresses={[transaction.payer]} />
        )
      },
      recipients: {
        key: "recipients",
        title: "Recipients",
        style: { minWidth: "30rem" },
        renderContent: (transaction) => (
          <DisplayLabels
            contextWalletAddresses={map(transaction.payments, "payee")}
          />
        )
      },
      memo: {
        key: "memo",
        title: "Memo",
        renderContent: (transaction) => {
          const memos = compact(map(transaction.payments, "memo"));

          const firstMemo = head(memos);

          if (!firstMemo || firstMemo === DEFAULT_PAYMENT_MEMO) return null;

          const decodedMemo = Base64.decode(firstMemo);

          const linkedCommissionReport = find(
            values(commissionReports),
            (commissionReport) => endsWith(commissionReport.uuid, decodedMemo)
          );

          if (linkedCommissionReport) {
            return (
              <Link
                className="text-sm hover:text-gray-600 truncate font-semibold dark:hover:text-white dark:hover:underline"
                to={`/workspace/commission-reports/${linkedCommissionReport.uuid}`}
              >
                {getCommissionReportTitle(linkedCommissionReport)}
              </Link>
            );
          }

          return decodedMemo;
        }
      },
      status: {
        key: "status",
        title: "Status",
        sortIteratee: "status",
        renderContent: (transaction) => capitalize(transaction.status)
      },
      open: {
        key: "open",
        title: "Details",
        renderContent: (transaction) => (
          <button
            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
            onClick={() => {
              setCurrentTransaction(transaction);
              setIsSidebarOpen(true);
            }}
          >
            Open
          </button>
        )
      },
      explorer: {
        key: "explorer",
        title: "Explorer",
        style: { minWidth: "7rem" },
        renderContent: (transaction) => (
          <ExternalLink
            url={`https://explorer.helium.com/txns/${transaction.hash}`}
            text="Open"
          />
        )
      },
      blockHeight: {
        key: "blockHeight",
        title: "Block height",
        style: { minWidth: "10rem" },
        renderContent: (transaction) => (
          <ExternalLink
            url={`https://explorer.helium.com/blocks/${transaction.block}`}
            text={transaction.block.toLocaleString()}
          />
        )
      }
    }),
    [commissionReports, displayInCurrency]
  );

  const sortedTransactions = useMemo(() => {
    let sortedTransactions = uniqBy(
      flatten(
        values(pick(paymentTransactionsByWallet, walletAddressesToFetch))
      ),
      "hash"
    );

    if (sortOrder) {
      sortedTransactions = orderBy(
        sortedTransactions,
        [sortOrder.iteratee],
        [sortOrder.order]
      );
    }

    return sortedTransactions;
  }, [paymentTransactionsByWallet, sortOrder, walletAddressesToFetch]);

  const renderPaymentsTable = useMemo(() => {
    if (isLoading && isEmpty(sortedTransactions))
      return <CenteredLoading className="h-96" />;

    if (isEmpty(walletAddressesToFetch)) return null;

    return (
      <EntityDataTable<PaymentTransaction>
        paginationKey={paginationKey}
        entity={Entity.transaction}
        items={sortedTransactions}
        properties={properties}
      />
    );
  }, [
    isLoading,
    paginationKey,
    properties,
    sortedTransactions,
    walletAddressesToFetch
  ]);

  return (
    <>
      <TablePropertiesSidebar<PaymentTransaction>
        entity={Entity.transaction}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      <div className={classNames("px-4 pb-4", className)}>
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          <span className="flex-1">{title}</span>

          {!isLoading && !isEmpty(walletAddressesToFetch) && (
            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                }
              ]}
            />
          )}
        </h3>
      </div>

      {renderPaymentsTable}

      <PaymentTransactionSidebar
        transaction={currentTransaction}
        open={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setCurrentTransaction(null);
        }}
      />
    </>
  );
};

export default PaymentTransactionsDataTable;

//
// Utils
//

export const PAYMENT_TRANSACTIONS_MAX_WALLETS_TO_FETCH = 10;
